import { CheckCircle, ExpandMore } from '@mui/icons-material';
import { alpha, Box, MenuItem, Select, Stack, styled, Tooltip, Typography, useTheme } from '@mui/material';
import { get, isNull, truncate } from 'lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { TypePlanPrice } from 'src/utils/constants/enum';
import { AuthRouteKeys, NoAuthRouteKeys } from 'src/utils/constants/routes';
import { handleQueryParamFromObject } from 'src/utils/helpers/queryParam';
import {
  getStringOrDefault,
  renderNumberOrDefault,
  renderPriceOrDefault,
  renderTextOrDefault,
} from 'src/utils/helpers/stringHelpers';

// Utilities
import { CreditCoinIcon } from 'src/components/Icons';
import { useQueryParams } from 'src/hooks/useQueryParam';
import { IPlanDetail } from 'src/utils/typings/response';
import { useMutateCalculatePricePlan } from 'src/services/mutate';
import ButtonWithLoading from 'src/components/common/ButtonWithLoading';
import { SUCCESS_CODE } from 'src/utils/constants/statusCode';
import PopupChangePlan from 'src/features/Pricing/PopupChangePlan';
import { UsageInformation } from 'src/model/payment';
import { getCookie } from 'src/utils/helpers/cookies';
import { AFFILIATE_PROGRAM_ID } from 'src/utils/constants/config';
import ImageComponent from 'src/components/common/Image';
import { ICON_GIFT_SPECIAL_PLAN } from 'src/utils/constants/imagesUrl';
import PopupConfirmSpecialTrial from 'src/features/Pricing/PopupConfirmSpecialTrial';

const StyledBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.default,
  border: `1px solid ${theme.palette.primary.main}`,
  borderRadius: 12,
}));

const StyledSelect = styled(Select)(({ theme }) => ({
  borderRadius: '100px',
  backgroundColor: alpha(theme.palette.primary.main, 0.07),
  '.MuiSelect-select': {
    padding: '8px 12px',
    color: theme.palette.primary.main,
    fontSize: 12,
  },
  '.MuiSelect-icon': {
    color: theme.palette.primary.main,
  },
  '&:before': {
    borderBottom: 'none',
  },
}));

const SpecialPlanCard = ({
  planDetail,
  campaignSlug = '',
  useInPublicPage = false,
  dataUsage,
}: {
  planDetail: IPlanDetail;
  campaignSlug?: string;
  useInPublicPage?: boolean; // Show on Campaign Page
  dataUsage?: UsageInformation;
}) => {
  const theme = useTheme();
  const query = useQueryParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { mutateAsync, isPending } = useMutateCalculatePricePlan();
  const isExistedPlan = dataUsage?.status_code === SUCCESS_CODE.GET && dataUsage?.data?.status === 'active';
  const isPlanFreeTrial = planDetail.is_trial;
  const affiliateCode = getCookie(AFFILIATE_PROGRAM_ID + '_affiliate_referral');
  const SPECIAL_PLAN_DEAL_WEBINAR_04DEC = 171;

  const [openConfirmTrial, setOpenConfirmTrial] = useState<boolean>(false);
  const [paramsChangePlan, setParamsChangePlan] = useState<{
    priceDifference: number;
    open: boolean;
    priceId: string;
  }>({
    priceDifference: 0,
    open: false,
    priceId: '',
  });

  const UNUSUAL_LOGIC_TO_PLEASE_THE_BUSINESS__TRIAL_DURATION =
    planDetail.trial_duration === 7 ? 5 : planDetail.trial_duration;

  const defaultType = () => {
    if (planDetail?.price_monthly) {
      return TypePlanPrice.Month;
    }

    if (planDetail?.price_quarterly) {
      return TypePlanPrice.Quarter;
    }

    if (planDetail?.price_half_yearly) {
      return TypePlanPrice.HalfYear;
    }

    if (planDetail?.price_yearly) {
      return TypePlanPrice.Year;
    }

    return null;
  };

  const [type, setType] = useState<TypePlanPrice | null>(defaultType());

  const redirectCheckout = async () => {
    if (isPlanFreeTrial) {
      setOpenConfirmTrial(true);
      return;
    }

    if ((isExistedPlan && dataUsage.data.is_trial) || !isExistedPlan) {
      const params = handleQueryParamFromObject({
        planId: planDetail.id,
        campaignSlug: campaignSlug,
        type,
      });
      navigate({ pathname: AuthRouteKeys.Checkout, search: params });
      return;
    } else {
      const responseGetPrice = await mutateAsync({ priceId: get(planDetail, `stripe_price_id_${type}`, '') });
      if (responseGetPrice?.status_code === SUCCESS_CODE.GET) {
        setParamsChangePlan({
          priceDifference: responseGetPrice?.data?.price_difference || 0,
          open: true,
          priceId: get(planDetail, `stripe_price_id_${type}`, ''),
        });
      }
    }
  };

  const handleChoosePlan = () => {
    if (useInPublicPage) {
      const params = handleQueryParamFromObject({ ...query, campaign_slug: campaignSlug });
      window.open(`${NoAuthRouteKeys.Login}?${params}`, '_blank', 'noopener,noreferrer');
    } else {
      redirectCheckout();
    }
  };

  return (
    <StyledBox border={`1px solid ${theme.palette.divider}`} borderRadius="12px" height="100%">
      <PopupChangePlan
        priceDifference={paramsChangePlan.priceDifference}
        open={paramsChangePlan.open}
        priceId={paramsChangePlan.priceId}
        handleClose={() =>
          setParamsChangePlan({
            priceDifference: 0,
            open: false,
            priceId: '',
          })
        }
      />
      <PopupConfirmSpecialTrial
        open={openConfirmTrial}
        planId={planDetail.id}
        creditPoints={planDetail.credit_points_per_month || 0}
        campaignSlug={campaignSlug}
        affiliateCode={affiliateCode}
        handleClose={() => setOpenConfirmTrial(false)}
      />
      <Stack direction="row" justifyContent="space-between">
        <Box>
          <Tooltip title={renderTextOrDefault(planDetail.plan_name)} placement="top">
            <Typography
              variant="h6"
              fontWeight={600}
              sx={{
                overflow: 'hidden',
                display: '-webkit-box',
                WebkitLineClamp: '1',
                lineClamp: '1',
                WebkitBoxOrient: 'vertical',
              }}
            >
              {renderTextOrDefault(planDetail.plan_name)}
            </Typography>
          </Tooltip>
          <Box mt={1} />
          <Tooltip title={renderTextOrDefault(planDetail.short_description)} placement="top">
            <Typography
              variant="body2"
              color={'textTertiary.main'}
              sx={{
                height: '100px',
                overflow: 'hidden',
              }}
            >
              {truncate(getStringOrDefault(planDetail.short_description), {
                length: 180,
              })}
            </Typography>
          </Tooltip>
        </Box>
        <ImageComponent imgSrc={ICON_GIFT_SPECIAL_PLAN} height={80} />
      </Stack>
      <Stack my={2} spacing={3} direction="row" alignItems="center" justifyContent="space-between">
        {!isNull(type) && (
          <StyledSelect
            labelId="demo-simple-select-label"
            id="demo-simple-select"
            variant="filled"
            value={type}
            IconComponent={ExpandMore}
            onChange={(event) => {
              setType(event?.target?.value as TypePlanPrice);
            }}
          >
            {planDetail?.price_monthly && (
              <MenuItem value={TypePlanPrice.Month}>{t('pricing.plan_type.monthly')}</MenuItem>
            )}
            {planDetail?.price_quarterly && (
              <MenuItem value={TypePlanPrice.Quarter}>{t('pricing.plan_type.quarterly')}</MenuItem>
            )}
            {planDetail?.price_half_yearly && (
              <MenuItem value={TypePlanPrice.HalfYear}>{t('pricing.plan_type.half_yearly')}</MenuItem>
            )}
            {planDetail?.price_yearly && (
              <MenuItem value={TypePlanPrice.Year}>{t('pricing.plan_type.yearly')}</MenuItem>
            )}
          </StyledSelect>
        )}
        {isNull(type) && <Typography variant="h5">$0</Typography>}
        {/* Set up Special plan for 04 Dec - REMOVE LATER */}
        {!isNull(type) && !isPlanFreeTrial && planDetail.id === SPECIAL_PLAN_DEAL_WEBINAR_04DEC && (
          <Typography variant="h5">
            <Typography
              color={'textTertiary.main'}
              variant="body1"
              component="span"
              fontSize={'1.25rem'}
              sx={{ textDecoration: 'line-through', marginRight: 1 }}
            >
              $1500
            </Typography>
            ${renderPriceOrDefault(get(planDetail, `price_${type}`, 0))} /
            <Typography color={'textTertiary.main'} variant="body2" component="span">
              {t(`pricing.plan_time.${type}`)}
            </Typography>
          </Typography>
        )}
        {!isNull(type) && !isPlanFreeTrial && planDetail.id !== SPECIAL_PLAN_DEAL_WEBINAR_04DEC && (
          <Typography variant="h5">
            ${renderPriceOrDefault(get(planDetail, `price_${type}`, 0))} /{' '}
            <Typography color={'textTertiary.main'} variant="body2" component="span">
              {t(`pricing.plan_time.${type}`)}
            </Typography>
          </Typography>
        )}
        {!isNull(type) && isPlanFreeTrial && (
          <Typography variant="h5">
            <Typography
              color={'textTertiary.main'}
              variant="h6"
              component="span"
              sx={{ textDecoration: 'line-through' }}
            >
              ${renderPriceOrDefault(get(planDetail, `price_${type}`, 0))}
            </Typography>{' '}
            $0
          </Typography>
        )}
      </Stack>
      <Stack mb={2} spacing={1} direction="row" alignItems="center">
        <CreditCoinIcon width={20} />
        <Typography variant="subtitle2">
          {renderNumberOrDefault(planDetail.credit_points_per_month || 0)} {t('pricing.credit')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          / {t('pricing.plan_time.monthly')}
          {/* {isPlanFreeTrial
            ? `expires in ${UNUSUAL_LOGIC_TO_PLEASE_THE_BUSINESS__TRIAL_DURATION} days`
            : t('pricing.plan_time.monthly')} */}
        </Typography>
      </Stack>
      <ButtonWithLoading
        disabled={isPending || (isExistedPlan && isPlanFreeTrial)}
        isLoading={isPending}
        fullWidth
        color="primary"
        variant="contained"
        onClick={() => handleChoosePlan()}
      >
        {useInPublicPage ? t('pricing.join_now') : isExistedPlan ? t('pricing.change_plan') : t('pricing.choose_plan')}
      </ButtonWithLoading>
      <Stack spacing={1.5} direction="column" mt={2}>
        {planDetail.benefits.map((benefit, index) => (
          <Stack key={`benefit-${planDetail.id}-${index}`} spacing={1} direction="row">
            <CheckCircle color="success" fontSize="small" />
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                width: '90%',
                overflowWrap: 'break-word',
              }}
            >
              {benefit}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </StyledBox>
  );
};

export default SpecialPlanCard;
